import React from 'react';
import { Icon } from '@rv-life/design-system';
import css from './DevToolsMenu.module.css';
import { useDevTools } from '../../DevToolsProvider';
import { POSITION_OPTIONS, DevToolsPosition } from '../../utils';

export type MenuView = 'main' | 'version-override';

interface DevToolsMenuProps {
	currentView: MenuView;
	onViewChange: (view: MenuView) => void;
	onClose: () => void;
}

const CodeBranchIcon = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M6 3C6 4.10457 5.10457 5 4 5C2.89543 5 2 4.10457 2 3C2 1.89543 2.89543 1 4 1C5.10457 1 6 1.89543 6 3Z"
			stroke="currentColor"
			strokeWidth="1.5"
		/>
		<path
			d="M14 3C14 4.10457 13.1046 5 12 5C10.8954 5 10 4.10457 10 3C10 1.89543 10.8954 1 12 1C13.1046 1 14 1.89543 14 3Z"
			stroke="currentColor"
			strokeWidth="1.5"
		/>
		<path
			d="M6 13C6 14.1046 5.10457 15 4 15C2.89543 15 2 14.1046 2 13C2 11.8954 2.89543 11 4 11C5.10457 11 6 11.8954 6 13Z"
			stroke="currentColor"
			strokeWidth="1.5"
		/>
		<path d="M4 5V11" stroke="currentColor" strokeWidth="1.5" />
		<path d="M12 5V7C12 8.10457 11.1046 9 10 9H4" stroke="currentColor" strokeWidth="1.5" />
	</svg>
);

const DevToolsMenu = ({ currentView, onViewChange, onClose }: DevToolsMenuProps) => {
	const { mfeVersions, hideDevTools, position, setPosition } = useDevTools();

	const overrideCount = Object.values(mfeVersions).filter(
		(mfe) => mfe.version && mfe.version.trim() !== ''
	).length;

	const handleHideDevTools = () => {
		hideDevTools();
		onClose();
	};

	const handlePositionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setPosition(e.target.value as DevToolsPosition);
	};

	if (currentView !== 'main') {
		return null;
	}

	return (
		<div className={css.devToolsMenu}>
			<ul className={css.menuList}>
				<li>
					<button
						className={css.menuItem}
						onClick={() => onViewChange('version-override')}
						type="button"
					>
						<span className={css.menuItemIcon}>
							<CodeBranchIcon />
						</span>
						<span className={css.menuItemLabel}>Version Override</span>
						{overrideCount > 0 && <span className={css.badge}>{overrideCount}</span>}
						<span className={css.menuItemChevron}>
							<Icon name="chevronRight" />
						</span>
					</button>
				</li>
			</ul>

			<div className={css.divider} />

			<div className={css.positionRow}>
				<span className={css.positionLabel}>Position</span>
				<select
					className={css.positionSelect}
					value={position}
					onChange={handlePositionChange}
					aria-label="Dev Tools Position"
				>
					{POSITION_OPTIONS.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</select>
			</div>

			<div className={css.divider} />

			<div className={css.footer}>
				<button className={css.footerButton} onClick={handleHideDevTools} type="button">
					<Icon name="visibilityOff" />
					<span>Hide</span>
				</button>
				<a
					href="https://rvlife.atlassian.net/wiki/x/KICTOQ"
					target="_blank"
					rel="noopener noreferrer"
					className={css.footerButton}
					aria-label="Learn more about Dev Tools"
					title="Learn more about Dev Tools"
				>
					<Icon name="question" color="#c1c1c1" />
				</a>
			</div>
		</div>
	);
};

export default DevToolsMenu;
